import { Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./cursor";
import type { Tree } from "@lezer/common";

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

const inlineCodeMark = Decoration.mark({ class: "cm-md-inline-code" });
const codeBlockLine = Decoration.line({ class: "cm-md-code-block" });

/**
 * Build decorations for inline code and fenced code blocks.
 *
 * **Inline code (`InlineCode` nodes):**
 * When the cursor is NOT on the same line, the backtick `CodeMark` tokens
 * are hidden and the content between them gets the `.cm-md-inline-code` class.
 *
 * **Fenced code blocks (`FencedCode` nodes):**
 * When the cursor is NOT anywhere inside the block's range, the opening and
 * closing fence lines (``` markers) are hidden and each content line gets a
 * `.cm-md-code-block` line decoration. If the cursor is anywhere inside the
 * block, the entire block is shown as raw markdown.
 */
export function buildCodeDecorations(
  state: EditorState,
  tree: Tree,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  const cursorLine = cursor.cursorLine;

  tree.iterate({
    from: range.from,
    to: range.to,
    enter(node) {
      if (node.name === "InlineCode") {
        buildInlineCodeDecorations(state, node, cursorLine, decorations);
        return false; // don't descend into children
      }

      if (node.name === "FencedCode") {
        buildFencedCodeDecorations(state, node, cursor, decorations);
        return false; // don't descend into children
      }
    },
  });
}

function buildInlineCodeDecorations(
  state: EditorState,
  node: { from: number; to: number; node: any },
  cursorLine: number,
  decorations: DecorationEntry[],
): void {
  const nodeLine = state.doc.lineAt(node.from).number;

  // If cursor is on this line, show raw markdown
  if (cursorLine === nodeLine) return;

  // Skip inline code inside headings to prevent overlapping decorations
  let ancestor = node.node.parent;
  while (ancestor) {
    if (ancestor.name.startsWith("ATXHeading")) return;
    ancestor = ancestor.parent;
  }

  // Collect CodeMark positions (opening and closing backticks)
  const marks: Array<{ from: number; to: number }> = [];
  const childCursor = node.node.cursor();
  if (childCursor.firstChild()) {
    do {
      if (childCursor.name === "CodeMark") {
        marks.push({ from: childCursor.from, to: childCursor.to });
      }
    } while (childCursor.nextSibling());
  }

  if (marks.length < 2) return;

  // Hide opening and closing backticks
  for (const mark of marks) {
    decorations.push({
      from: mark.from,
      to: mark.to,
      decoration: Decoration.replace({}),
    });
  }

  // Style the content between the backticks
  const contentFrom = marks[0].to;
  const contentTo = marks[marks.length - 1].from;
  if (contentFrom < contentTo) {
    decorations.push({
      from: contentFrom,
      to: contentTo,
      decoration: inlineCodeMark,
    });
  }
}

function buildFencedCodeDecorations(
  state: EditorState,
  node: { from: number; to: number; node: any },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  // For multi-line blocks: check if cursor is anywhere inside the block range
  if (cursor.head >= node.from && cursor.head <= node.to) return;

  // Also check if cursor anchor is inside (for selections spanning into the block)
  if (cursor.anchor >= node.from && cursor.anchor <= node.to) return;

  // Collect the fence lines (CodeMark children — opening and closing ```)
  const fenceLines: Array<{ from: number; to: number }> = [];
  const childCursor = node.node.cursor();
  if (childCursor.firstChild()) {
    do {
      if (childCursor.name === "CodeMark") {
        fenceLines.push({ from: childCursor.from, to: childCursor.to });
      }
    } while (childCursor.nextSibling());
  }

  // Hide the opening fence line (``` + optional language info)
  if (fenceLines.length >= 1) {
    const openFence = fenceLines[0];
    const openLine = state.doc.lineAt(openFence.from);
    // Replace the entire opening line content (fence + language tag)
    decorations.push({
      from: openLine.from,
      to: openLine.to,
      decoration: Decoration.replace({}),
    });
  }

  // Hide the closing fence line
  if (fenceLines.length >= 2) {
    const closeFence = fenceLines[fenceLines.length - 1];
    const closeLine = state.doc.lineAt(closeFence.from);
    decorations.push({
      from: closeLine.from,
      to: closeLine.to,
      decoration: Decoration.replace({}),
    });
  }

  // Apply line decorations to content lines (lines between fences)
  if (fenceLines.length >= 2) {
    const openLine = state.doc.lineAt(fenceLines[0].from);
    const closeLine = state.doc.lineAt(fenceLines[fenceLines.length - 1].from);

    for (let lineNum = openLine.number + 1; lineNum < closeLine.number; lineNum++) {
      const line = state.doc.line(lineNum);
      decorations.push({
        from: line.from,
        to: line.from, // line decorations use from === to === line start
        decoration: codeBlockLine,
      });
    }
  }
}
